<script>
import axios from 'axios';

// MedUp: "Филиалы" tab on the inbox settings page — pick which branches this channel/bot serves.
// Writes branch_inboxes via /inboxes/:id/branches (see InboxesController#branches/update_branches).
export default {
  name: 'InboxBranches',
  props: {
    inboxId: { type: [Number, String], required: true },
  },
  data() {
    return {
      branches: [],
      defaultBranchId: null,
      loading: true,
      saving: false,
      saved: false,
    };
  },
  computed: {
    accountId() {
      return this.$route.params.accountId;
    },
    baseUrl() {
      return `/api/v1/accounts/${this.accountId}/inboxes/${this.inboxId}`;
    },
    selectedIds() {
      return this.branches.filter(b => b.linked).map(b => b.id);
    },
  },
  mounted() {
    this.fetch();
  },
  methods: {
    // Chatwoot v4 auth: devise-token-auth headers are stored in the cw_d_session_info cookie
    // (read fresh each call so token rotation is picked up). Raw axios doesn't carry them.
    authHeaders() {
      const raw = document.cookie
        .split(';')
        .map(c => c.trim())
        .find(c => c.startsWith('cw_d_session_info='));
      if (!raw) return {};
      try {
        const i = JSON.parse(decodeURIComponent(raw.split('=').slice(1).join('=')));
        return {
          'access-token': i['access-token'],
          'token-type': i['token-type'],
          client: i.client,
          expiry: String(i.expiry),
          uid: i.uid,
        };
      } catch (e) {
        return {};
      }
    },
    async fetch() {
      this.loading = true;
      try {
        const { data } = await axios.get(`${this.baseUrl}/branches`, {
          headers: this.authHeaders(),
        });
        this.branches = (data.branches || []).map(b => ({ ...b }));
        const def = this.branches.find(b => b.is_default);
        this.defaultBranchId = def ? def.id : null;
      } finally {
        this.loading = false;
      }
    },
    toggle(branch) {
      branch.linked = !branch.linked;
      if (!branch.linked && this.defaultBranchId === branch.id) {
        this.defaultBranchId = null;
      }
      this.saved = false;
    },
    setDefault(branch) {
      this.defaultBranchId = branch.id;
      this.saved = false;
    },
    async save() {
      this.saving = true;
      this.saved = false;
      try {
        await axios.put(
          `${this.baseUrl}/branches`,
          {
            branch_ids: this.selectedIds,
            default_branch_id: this.defaultBranchId,
          },
          { headers: this.authHeaders() }
        );
        this.saved = true;
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<template>
  <div class="py-4">
    <p class="mb-4 text-sm text-n-slate-11">
      Отметьте филиалы, которые обслуживает этот канал. Уведомления по этим
      филиалам будут уходить через него. «Основной» — какой канал этого типа
      выбирается для филиала первым в каскаде.
    </p>

    <div v-if="loading" class="text-sm text-n-slate-10">Загрузка…</div>
    <div v-else-if="!branches.length" class="text-sm text-n-slate-10">
      Нет филиалов. Синхронизируйте их из Bitrix24 в разделе «Данные → Филиалы».
    </div>

    <div v-else class="flex flex-col gap-1.5">
      <label
        v-for="b in branches"
        :key="b.id"
        class="flex items-center gap-3 px-3 py-2.5 border rounded-lg cursor-pointer border-n-weak hover:bg-n-alpha-1"
      >
        <input
          type="checkbox"
          :checked="b.linked"
          class="accent-woot-500"
          @change="toggle(b)"
        />
        <span class="flex-1 text-sm text-n-slate-12">{{ b.name }}</span>
        <button
          v-if="b.linked"
          type="button"
          class="px-2 py-1 text-xs rounded-md"
          :class="
            defaultBranchId === b.id
              ? 'bg-n-teal-3 text-n-teal-11'
              : 'text-n-slate-10 hover:bg-n-alpha-2'
          "
          @click.prevent="setDefault(b)"
        >
          {{ defaultBranchId === b.id ? 'основной ✓' : 'сделать основным' }}
        </button>
      </label>
    </div>

    <div v-if="!loading" class="flex items-center gap-3 mt-5">
      <woot-button :is-loading="saving" :disabled="loading" @click="save">
        Сохранить
      </woot-button>
      <span v-if="saved" class="text-sm text-n-teal-11">Сохранено</span>
    </div>
  </div>
</template>
